import { z } from "zod";
import { ORG_ROLES, type AttentionKind, type OrgRole } from "../enums";
import { LIMITS } from "../limits";
import { type ActorRef, email, nonEmptyPatch, optionalText, requiredText, uuid } from "./common";
import type { TorSummary } from "./tors";
import type { DashboardV2Fields } from "./v1-additions";

// ---------- Requests ----------

export const updateOrgBody = nonEmptyPatch({
  name: requiredText(LIMITS.orgName, "Organisation name").optional(),
  defaultTorBoilerplate: optionalText(LIMITS.boilerplate, "The clauses").optional(),
});
export type UpdateOrgBody = z.infer<typeof updateOrgBody>;

export const updateMemberBody = z.object({ role: z.enum(ORG_ROLES) }).strict();
export type UpdateMemberBody = z.infer<typeof updateMemberBody>;

export const createInvitationBody = z.object({ email, role: z.enum(ORG_ROLES) }).strict();
export type CreateInvitationBody = z.infer<typeof createInvitationBody>;

export const activityQuery = z
  .object({
    contractId: uuid.optional(),
    cursor: z.string().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(LIMITS.activityPageMax).default(LIMITS.activityPageDefault),
  })
  .strict();
export type ActivityQuery = z.infer<typeof activityQuery>;

// ---------- Responses ----------

export type Org = {
  id: string;
  name: string;
  defaultTorBoilerplate: string;
  createdAt: string;
  updatedAt: string;
};

export type Member = { userId: string; name: string; email: string; role: OrgRole; joinedAt: string };

export type Invitation = {
  id: string;
  email: string;
  role: OrgRole;
  invitedBy: ActorRef;
  createdAt: string;
  expiresAt: string;
};

export type AuditEvent = {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  contractId: string | null;
  summary: string;
  actor: ActorRef;
  createdAt: string;
};

export type ActivityResponse = { items: AuditEvent[]; nextCursor: string | null };

export type AttentionItem = {
  kind: AttentionKind;
  torId: string;
  torTitle: string;
  version: number;
  contractId: string;
  contractName: string;
  contractReference: string;
  /** signedOffAt | submittedAt | decidedAt | updatedAt */
  since: string;
  /** whole days since `since` */
  days: number;
};

export type DashboardResponse = {
  attention: AttentionItem[];
  recentTors: (TorSummary & { contractName: string; contractReference: string })[];
  counts: { openContracts: number; torsWithFunder: number; drafts: number };
} & DashboardV2Fields;
